import { motion } from 'framer-motion';
import { ArrowRight, Check, Droplets, Hammer, ShieldCheck, ParkingSquare, Palette } from 'lucide-react';

export const ServicesSection = ({ onQuoteClick }) => {
  return (
    <section id="services" className="py-24 bg-neutral-900 relative">
      {/* Red stripe accent */}
      <div className="absolute top-0 left-0 right-0 h-2 bg-red-600" />

      {/* Rhino watermark - decorative */}
      <div className="absolute bottom-0 left-0 opacity-[0.03] pointer-events-none" aria-hidden="true">
        <img src="/images/logo.jpg" alt="" role="presentation" className="w-[500px] h-[500px] object-contain" />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Section header with logo */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <img src="/images/logo.jpg" alt="Rhino Seal" className="w-16 h-16 rounded-full border-2 border-red-600" />
          </div>
          <span className="text-red-500 text-sm font-semibold uppercase tracking-wider">
            Our Services
          </span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mt-4 mb-6">
            PROFESSIONAL
            <span className="text-red-500"> PAVEMENT</span> CARE
          </h2>
          <p className="text-xl text-neutral-400 max-w-3xl mx-auto">
            From residential driveways to commercial parking lots, we provide comprehensive 
            asphalt maintenance services throughout Central Massachusetts and Metro West Boston.
          </p>
        </motion.div>

        {/* Services grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Crack Filling */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="group bg-neutral-950 border-2 border-gray-800 rounded-sm overflow-hidden"
            whileHover={{ 
              borderColor: '#dc2626',
              boxShadow: '0 0 30px rgba(220, 38, 38, 0.2)'
            }}
            data-testid="service-crack-filling"
          >
            <div className="relative h-48 overflow-hidden bg-neutral-800 flex items-center justify-center">
              <motion.div
                className="w-24 h-24 rounded-full bg-neutral-700 border-4 border-red-600 flex items-center justify-center"
                whileHover={{ scale: 1.1 }}
              >
                <Droplets className="w-12 h-12 text-red-500" />
              </motion.div>
              <div className="absolute top-4 left-4 px-3 py-1 bg-red-600 text-white rounded-sm text-sm font-bold uppercase">
                Most Important
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <Droplets className="w-6 h-6 text-red-500" />
                <h3 className="text-2xl font-black text-white">Crack Filling</h3>
              </div>
              <p className="text-neutral-400 mb-4 leading-relaxed">
                Crack Filling is the most important part of any asphalt maintenance project. As asphalt begins to breakdown and become porous, water penetrates the surface and pushes stones from the asphalt leaving a void.
              </p>
              <p className="text-neutral-500 mb-6 leading-relaxed text-sm">
                Cracks can be filled with cold pour material (hand troweled) or hot pour method (300-400 degrees). Hot Pour is more costly but a much more permanent solution.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Prevents water penetration
                </li>
                <li className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Cold pour & hot pour options
                </li>
                <li className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Long-lasting protection
                </li>
              </ul>
              <motion.button
                onClick={onQuoteClick}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider py-3 rounded-sm flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02, boxShadow: '0 0 20px rgba(220, 38, 38, 0.4)' }}
                whileTap={{ scale: 0.98 }}
              >
                Get a Quote <ArrowRight className="w-4 h-4" />
              </motion.button>
            </div>
          </motion.div>

          {/* Patchwork */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="group bg-neutral-950 border-2 border-gray-800 rounded-sm overflow-hidden"
            whileHover={{ 
              borderColor: '#dc2626',
              boxShadow: '0 0 30px rgba(220, 38, 38, 0.2)'
            }}
            data-testid="service-patchwork"
          >
            <div className="relative h-48 overflow-hidden bg-neutral-800 flex items-center justify-center">
              <motion.div
                className="w-24 h-24 rounded-full bg-neutral-700 border-4 border-gray-600 flex items-center justify-center"
                whileHover={{ scale: 1.1 }}
              >
                <Hammer className="w-12 h-12 text-gray-400" />
              </motion.div>
              <div className="absolute top-4 left-4 px-3 py-1 bg-gray-600 text-white rounded-sm text-sm font-bold uppercase">
                Total Breakdown Solution
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <Hammer className="w-6 h-6 text-gray-400" />
                <h3 className="text-2xl font-black text-white">Patchwork</h3>
              </div>
              <p className="text-neutral-400 mb-4 leading-relaxed">
                Patch work will be required if asphalt has reached total breakdown. The good news is that this can be prevented through proper maintenance!
              </p>
              <p className="text-neutral-500 mb-6 leading-relaxed text-sm">
                When cracks reach the sub-base, the old asphalt must be excavated and replaced. The area is saw cut, coated with bonding agent, then new material is placed and compacted.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Professional excavation
                </li>
                <li className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Quality bonding agents
                </li>
                <li className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Sealed joints included
                </li>
              </ul>
              <motion.button
                onClick={onQuoteClick}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider py-3 rounded-sm flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02, boxShadow: '0 0 20px rgba(220, 38, 38, 0.4)' }}
                whileTap={{ scale: 0.98 }}
              >
                Get a Quote <ArrowRight className="w-4 h-4" />
              </motion.button>
            </div>
          </motion.div>

          {/* Asphalt Sealing */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="group bg-neutral-950 border-2 border-gray-800 rounded-sm overflow-hidden"
            whileHover={{ 
              borderColor: '#dc2626',
              boxShadow: '0 0 30px rgba(220, 38, 38, 0.2)'
            }}
            data-testid="service-asphalt-sealing"
          >
            <div className="relative h-48 overflow-hidden bg-neutral-800 flex items-center justify-center">
              <motion.div
                className="w-24 h-24 rounded-full bg-neutral-700 border-4 border-green-600 flex items-center justify-center"
                whileHover={{ scale: 1.1 }}
              >
                <ShieldCheck className="w-12 h-12 text-green-500" />
              </motion.div>
              <div className="absolute top-4 left-4 px-3 py-1 bg-green-600 text-white rounded-sm text-sm font-bold uppercase">
                First Step in Prevention
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <ShieldCheck className="w-6 h-6 text-green-500" />
                <h3 className="text-2xl font-black text-white">Asphalt Sealing</h3>
              </div>
              <p className="text-neutral-400 mb-4 leading-relaxed">
                Sealcoating is the first step in preventing asphalt breakdown. The bituminous materials in new asphalt act as a bonding agent to hold your driveway together.
              </p>
              <p className="text-neutral-500 mb-6 leading-relaxed text-sm">
                Gas, oil spills, freeze-thaw cycles, and sun bleaching remove essential materials. Sealing creates a protective barrier. Can be applied by hand or sprayed with professional equipment.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Prevents water damage
                </li>
                <li className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Protects against UV rays
                </li>
                <li className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Enhances curb appeal
                </li>
              </ul>
              <motion.button
                onClick={onQuoteClick}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider py-3 rounded-sm flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02, boxShadow: '0 0 20px rgba(220, 38, 38, 0.4)' }}
                whileTap={{ scale: 0.98 }}
              >
                Get a Quote <ArrowRight className="w-4 h-4" />
              </motion.button>
            </div>
          </motion.div>

          {/* Line Striping */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="group bg-neutral-950 border-2 border-gray-800 rounded-sm overflow-hidden"
            whileHover={{ 
              borderColor: '#dc2626',
              boxShadow: '0 0 30px rgba(220, 38, 38, 0.2)'
            }}
            data-testid="service-line-striping"
          >
            <div className="relative h-48 overflow-hidden bg-neutral-800 flex items-center justify-center">
              <motion.div
                className="w-24 h-24 rounded-full bg-neutral-700 border-4 border-blue-600 flex items-center justify-center"
                whileHover={{ scale: 1.1 }}
              >
                <ParkingSquare className="w-12 h-12 text-blue-500" />
              </motion.div>
              <div className="absolute top-4 left-4 px-3 py-1 bg-blue-600 text-white rounded-sm text-sm font-bold uppercase">
                Parking Lot Markings
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <ParkingSquare className="w-6 h-6 text-blue-500" />
                <h3 className="text-2xl font-black text-white">Line Striping</h3>
              </div>
              <p className="text-neutral-400 mb-4 leading-relaxed">
                Line striping and parking lot marking for areas where traffic patterns are confusing or problematic. All markings done with high visibility paints.
              </p>
              <p className="text-neutral-500 mb-6 leading-relaxed text-sm">
                Custom painting available but may require additional time for stencil manufacturing and paint blending.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Handicap parking spaces
                </li>
                <li className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Directional arrows
                </li>
                <li className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Fire lanes & no parking
                </li>
              </ul>
              <motion.button
                onClick={onQuoteClick}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider py-3 rounded-sm flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02, boxShadow: '0 0 20px rgba(220, 38, 38, 0.4)' }}
                whileTap={{ scale: 0.98 }}
              >
                Get a Quote <ArrowRight className="w-4 h-4" />
              </motion.button>
            </div>
          </motion.div>

          {/* Color Coating - Full width */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="lg:col-span-2 group bg-neutral-950 border-2 border-gray-800 rounded-sm overflow-hidden"
            whileHover={{ 
              borderColor: '#dc2626',
              boxShadow: '0 0 30px rgba(220, 38, 38, 0.2)'
            }}
            data-testid="service-color-coating"
          >
            <div className="grid lg:grid-cols-2">
              <div className="relative h-48 lg:h-auto overflow-hidden bg-neutral-800 flex items-center justify-center">
                <motion.div
                  className="w-24 h-24 lg:w-32 lg:h-32 rounded-full bg-neutral-700 border-4 border-purple-600 flex items-center justify-center"
                  whileHover={{ scale: 1.1 }}
                >
                  <Palette className="w-12 h-12 lg:w-16 lg:h-16 text-purple-500" />
                </motion.div>
                <div className="absolute top-4 left-4 px-3 py-1 bg-purple-600 text-white rounded-sm text-sm font-bold uppercase">
                  Custom Colors Available
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Palette className="w-6 h-6 text-purple-500" />
                  <h3 className="text-2xl font-black text-white">Color Coating</h3>
                </div>
                <p className="text-neutral-400 mb-4 leading-relaxed">
                  Black has long been the sealcoating industry standard but custom color coatings are now available. Custom colors have made their way from tennis and basketball courts to driveways and parking areas.
                </p>
                <p className="text-neutral-500 mb-6 leading-relaxed text-sm">
                  Pricing will vary based on color and area but nothing takes a homes curb appeal to the next level like a custom color coating!
                </p>
                <ul className="grid sm:grid-cols-2 gap-2 mb-6">
                  <li className="flex items-center gap-2 text-sm text-neutral-300">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    Custom color options
                  </li>
                  <li className="flex items-center gap-2 text-sm text-neutral-300">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    Tennis & basketball courts
                  </li>
                  <li className="flex items-center gap-2 text-sm text-neutral-300">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    Driveways & parking areas
                  </li>
                  <li className="flex items-center gap-2 text-sm text-neutral-300">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    Ultimate curb appeal
                  </li>
                </ul>
                <motion.button
                  onClick={onQuoteClick}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider py-3 px-8 rounded-sm flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02, boxShadow: '0 0 20px rgba(220, 38, 38, 0.4)' }}
                  whileTap={{ scale: 0.98 }}
                >
                  Get a Quote <ArrowRight className="w-4 h-4" />
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom CTA with Rhino */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center bg-gradient-to-r from-red-600/20 via-red-600/10 to-red-600/20 border-2 border-red-600/30 rounded-sm p-8"
          whileHover={{ borderColor: '#dc2626', boxShadow: '0 0 30px rgba(220, 38, 38, 0.2)' }}
        >
          <img src="/images/logo.jpg" alt="Rhino Seal" className="w-20 h-20 rounded-full border-4 border-red-600 mx-auto mb-4" />
          <h3 className="text-2xl font-black text-white mb-4">
            NOT SURE WHAT YOU NEED?
          </h3>
          <p className="text-neutral-400 mb-6">
            Our experts will assess your pavement and recommend the best solution for your budget.
          </p>
          <motion.button
            onClick={onQuoteClick}
            className="bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-wider px-8 py-4 rounded-sm inline-flex items-center gap-2"
            whileHover={{ scale: 1.05, boxShadow: '0 0 25px rgba(220, 38, 38, 0.5)' }}
            whileTap={{ scale: 0.95 }}
          >
            Schedule Free Assessment
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
